"use client";

import { useState, useCallback, useRef, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";
import { Label } from "@/app/components/ui/label";
import { Badge } from "@/app/components/ui/badge";
import { Alert, AlertDescription } from "@/app/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/app/components/ui/select";
import { Slider } from "@/app/components/ui/slider";
import { Checkbox } from "@/app/components/ui/checkbox";
import { 
  Image, 
  Upload, 
  Download, 
  RotateCcw, 
  Settings, 
  Info, 
  AlertTriangle, 
  CheckCircle,
  Maximize,
  Minimize,
  BarChart3,
  History,
  Save,
  Trash2,
  RefreshCw,
  Database,
  Lock,
  Clock,
  Fingerprint,
  Layers,
  Target,
  Sparkles,
  Users,
  UserCheck,
  FileText,
  Shield,
  Eye,
  EyeOff,
  Maximize2,
  Square,
  Monitor,
  Smartphone,
  Tablet,
  Camera,
  FileImage,
  Zap,
  Wand2,
  Palette,
  Crop,
  Move,
  RotateCw,
  FlipHorizontal,
  FlipVertical
} from "lucide-react";
import { toast } from "sonner";

interface ImageFile {
  id: string;
  file: File;
  preview: string;
  originalSize: { width: number; height: number };
  resizedSize: { width: number; height: number };
  timestamp: Date;
}

interface ResizeHistory {
  id: string;
  images: ImageFile[];
  timestamp: Date;
  settings: ResizeSettings;
}

interface ResizeSettings {
  width: number;
  height: number;
  maintainAspectRatio: boolean;
  quality: number;
  format: string;
  method: string;
}

type ResizeMethod = "lanczos" | "bilinear" | "nearest" | "bicubic";
type ImageFormat = "jpeg" | "png" | "webp" | "original";

const PRESET_SIZES = [
  { name: "Thumbnail", width: 150, height: 150 },
  { name: "Small", width: 300, height: 300 },
  { name: "Medium", width: 600, height: 600 },
  { name: "Large", width: 1200, height: 1200 },
  { name: "HD", width: 1920, height: 1080 },
  { name: "4K", width: 3840, height: 2160 },
  { name: "Instagram Square", width: 1080, height: 1080 },
  { name: "Instagram Story", width: 1080, height: 1920 },
  { name: "Facebook Cover", width: 1200, height: 630 },
  { name: "Twitter Header", width: 1500, height: 500 },
  { name: "LinkedIn Banner", width: 1584, height: 396 },
  { name: "YouTube Thumbnail", width: 1280, height: 720 },
  { name: "Profile Picture", width: 400, height: 400 },
  { name: "Blog Post", width: 800, height: 600 },
  { name: "Email Header", width: 600, height: 200 }
];

const DEVICE_PRESETS = [
  { name: "iPhone 15 Pro", width: 1179, height: 2556 },
  { name: "iPhone 15", width: 1170, height: 2532 },
  { name: "Samsung Galaxy S24", width: 1080, height: 2340 },
  { name: "iPad Pro", width: 2048, height: 2732 },
  { name: "iPad Air", width: 1640, height: 2360 },
  { name: "MacBook Pro 16\"", width: 3456, height: 2234 },
  { name: "MacBook Air 13\"", width: 2560, height: 1664 },
  { name: "Dell XPS 13", width: 1920, height: 1080 },
  { name: "Surface Pro", width: 2736, height: 1824 },
  { name: "4K Monitor", width: 3840, height: 2160 },
  { name: "1080p Monitor", width: 1920, height: 1080 },
  { name: "1440p Monitor", width: 2560, height: 1440 }
];

// Image Resize Utility Class
class ImageResizer {
  static async resizeImage(
    file: File,
    settings: ResizeSettings
  ): Promise<{ blob: Blob; width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      img.onload = () => {
        const { width, height, maintainAspectRatio } = settings;
        
        let newWidth = width;
        let newHeight = height;

        if (maintainAspectRatio) {
          const aspectRatio = img.width / img.height;
          if (width / height > aspectRatio) {
            newWidth = height * aspectRatio;
          } else {
            newHeight = width / aspectRatio;
          }
        }

        canvas.width = newWidth;
        canvas.height = newHeight;

        // Apply resize method
        switch (settings.method) {
          case 'lanczos':
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            break;
          case 'bilinear':
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'medium';
            break;
          case 'nearest':
            ctx.imageSmoothingEnabled = false;
            break;
          case 'bicubic':
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            break;
        }

        ctx.drawImage(img, 0, 0, newWidth, newHeight);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve({ blob, width: newWidth, height: newHeight });
            } else {
              reject(new Error('Failed to create blob'));
            }
          },
          settings.format === 'original' ? file.type : `image/${settings.format}`,
          settings.quality / 100
        );
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  }

  static async resizeMultiple(
    files: File[],
    settings: ResizeSettings
  ): Promise<{ blob: Blob; width: number; height: number; originalFile: File }[]> {
    const results = await Promise.all(
      files.map(async (file) => {
        const result = await this.resizeImage(file, settings);
        return {
          ...result,
          originalFile: file
        };
      })
    );
    return results;
  }

  static getFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  static calculateCompressionRatio(originalSize: number, newSize: number): number {
    return ((originalSize - newSize) / originalSize) * 100;
  }
}

export default function ImageResizePage() {
  const [images, setImages] = useState<ImageFile[]>([]);
  const [history, setHistory] = useState<ResizeHistory[]>([]);
  const [activeTab, setActiveTab] = useState<"preset" | "custom" | "device">("preset");
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Settings
  const [width, setWidth] = useState(800);
  const [height, setHeight] = useState(600);
  const [maintainAspectRatio, setMaintainAspectRatio] = useState(true);
  const [quality, setQuality] = useState(90);
  const [format, setFormat] = useState<ImageFormat>("original");
  const [method, setMethod] = useState<ResizeMethod>("lanczos");
  const [selectedPreset, setSelectedPreset] = useState(PRESET_SIZES[0]);
  const [selectedDevice, setSelectedDevice] = useState(DEVICE_PRESETS[0]);

  const handleFileUpload = useCallback((files: FileList | null) => {
    if (!files) return;

    const imageFiles = Array.from(files).filter(file => 
      file.type.startsWith('image/')
    );

    if (imageFiles.length === 0) {
      toast.error("Please select valid image files");
      return;
    }

    const newImages: ImageFile[] = imageFiles.map(file => ({
      id: `${Date.now()}-${Math.random()}`,
      file,
      preview: URL.createObjectURL(file),
      originalSize: { width: 0, height: 0 },
      resizedSize: { width: 0, height: 0 },
      timestamp: new Date()
    }));

    // Load image dimensions
    newImages.forEach(imageFile => {
      const img = new window.Image();
      img.onload = () => {
        imageFile.originalSize = { width: img.width, height: img.height };
        setImages(prev => [...prev, ...newImages]);
      };
      img.src = imageFile.preview;
    });

    toast.success(`Added ${imageFiles.length} image${imageFiles.length > 1 ? 's' : ''}`);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileUpload(e.dataTransfer.files);
  }, [handleFileUpload]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const resizeImages = useCallback(async () => {
    if (images.length === 0) {
      toast.error("Please upload images first");
      return;
    }

    setIsProcessing(true);

    try {
      const settings: ResizeSettings = {
        width,
        height,
        maintainAspectRatio,
        quality,
        format,
        method
      };

      const results = await ImageResizer.resizeMultiple(
        images.map(img => img.file),
        settings
      );

      // Update images with resized data
      const updatedImages = images.map((image, index) => ({
        ...image,
        resizedSize: { width: results[index].width, height: results[index].height }
      }));

      setImages(updatedImages);

      // Add to history
      const historyEntry: ResizeHistory = {
        id: Date.now().toString(),
        images: updatedImages,
        timestamp: new Date(),
        settings
      };

      setHistory(prev => [historyEntry, ...prev.slice(0, 49)]);

      toast.success(`Resized ${images.length} image${images.length > 1 ? 's' : ''} successfully!`);
    } catch (error) {
      toast.error("Failed to resize images");
      console.error(error);
    } finally {
      setIsProcessing(false);
    }
  }, [images, width, height, maintainAspectRatio, quality, format, method]);

  const downloadImage = useCallback(async (image: ImageFile) => {
    try {
      const settings: ResizeSettings = {
        width,
        height,
        maintainAspectRatio,
        quality,
        format,
        method
      };

      const result = await ImageResizer.resizeImage(image.file, settings);
      const url = URL.createObjectURL(result.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `resized_${image.file.name}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success("Image downloaded!");
    } catch (error) {
      toast.error("Failed to download image");
    }
  }, [width, height, maintainAspectRatio, quality, format, method]);

  const downloadAll = useCallback(async () => {
    if (images.length === 0) return;

    setIsProcessing(true);
    try {
      for (const image of images) {
        await downloadImage(image);
        // Small delay to prevent browser blocking
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      toast.success("All images downloaded!");
    } catch (error) {
      toast.error("Failed to download some images");
    } finally {
      setIsProcessing(false);
    }
  }, [images, downloadImage]);

  const clearImages = useCallback(() => {
    images.forEach(img => URL.revokeObjectURL(img.preview));
    setImages([]);
    toast.success("Images cleared!");
  }, [images]);

  const removeImage = useCallback((id: string) => {
    const image = images.find(img => img.id === id);
    if (image) {
      URL.revokeObjectURL(image.preview);
    }
    setImages(prev => prev.filter(img => img.id !== id));
    toast.success("Image removed!");
  }, [images]);

  const applyPreset = useCallback((preset: typeof PRESET_SIZES[0]) => {
    setWidth(preset.width);
    setHeight(preset.height);
    setSelectedPreset(preset);
    toast.success(`Applied ${preset.name} preset`);
  }, []);

  const applyDevicePreset = useCallback((device: typeof DEVICE_PRESETS[0]) => {
    setWidth(device.width);
    setHeight(device.height);
    setSelectedDevice(device);
    toast.success(`Applied ${device.name} preset`);
  }, []);

  const getTotalSize = useMemo(() => {
    return images.reduce((total, img) => total + img.file.size, 0);
  }, [images]);

  const getAverageDimensions = useMemo(() => {
    if (images.length === 0) return { width: 0, height: 0 };
    const totalWidth = images.reduce((sum, img) => sum + img.originalSize.width, 0);
    const totalHeight = images.reduce((sum, img) => sum + img.originalSize.height, 0);
    return {
      width: Math.round(totalWidth / images.length),
      height: Math.round(totalHeight / images.length)
    };
  }, [images]);

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">Image Resize Tool</h1>
        <p className="text-muted-foreground">
          Resize your images with precision. Choose from presets, custom dimensions, or device-specific sizes.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Upload Area */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Upload Images
              </CardTitle>
              <CardDescription>
                Drag and drop images or click to browse
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div
                className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center hover:border-muted-foreground/50 transition-colors cursor-pointer"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => fileInputRef.current?.click()}
              >
                <Image className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-lg font-medium mb-2">Drop images here or click to browse</p>
                <p className="text-sm text-muted-foreground">
                  Supports JPG, PNG, WebP, and other image formats
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e.target.files)}
                  className="hidden"
                />
              </div>
            </CardContent>
          </Card>

          {/* Resize Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Resize Settings
              </CardTitle>
              <CardDescription>
                Configure how your images will be resized
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as any)}>
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="preset">Presets</TabsTrigger>
                  <TabsTrigger value="custom">Custom</TabsTrigger>
                  <TabsTrigger value="device">Devices</TabsTrigger>
                </TabsList>

                <TabsContent value="preset" className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {PRESET_SIZES.map((preset) => (
                      <Button
                        key={preset.name}
                        variant={selectedPreset.name === preset.name ? "default" : "outline"}
                        size="sm"
                        onClick={() => applyPreset(preset)}
                        className="h-auto p-3 flex flex-col"
                      >
                        <span className="font-medium">{preset.name}</span>
                        <span className="text-sm text-foreground/80 font-medium">
                          {preset.width} × {preset.height}
                        </span>
                      </Button>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="custom" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Width: {width}px</Label>
                      <Slider
                        value={[width]}
                        onValueChange={(value) => setWidth(value[0])}
                        max={4000}
                        min={1}
                        step={1}
                        className="w-full"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Height: {height}px</Label>
                      <Slider
                        value={[height]}
                        onValueChange={(value) => setHeight(value[0])}
                        max={4000}
                        min={1}
                        step={1}
                        className="w-full"
                      />
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="maintainAspectRatio"
                      checked={maintainAspectRatio}
                      onCheckedChange={(checked) => setMaintainAspectRatio(checked as boolean)}
                    />
                    <Label htmlFor="maintainAspectRatio">Maintain Aspect Ratio</Label>
                  </div>
                </TabsContent>

                <TabsContent value="device" className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {DEVICE_PRESETS.map((device) => (
                      <Button
                        key={device.name}
                        variant={selectedDevice.name === device.name ? "default" : "outline"}
                        size="sm"
                        onClick={() => applyDevicePreset(device)}
                        className="h-auto p-3 flex flex-col"
                      >
                        <span className="font-medium">{device.name}</span>
                        <span className="text-sm text-foreground/80 font-medium">
                          {device.width} × {device.height}
                        </span>
                      </Button>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Quality: {quality}%</Label>
                  <Slider
                    value={[quality]}
                    onValueChange={(value) => setQuality(value[0])}
                    max={100}
                    min={1}
                    step={1}
                    className="w-full"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="format">Format</Label>
                  <Select value={format} onValueChange={(value) => setFormat(value as ImageFormat)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="original">Original</SelectItem>
                      <SelectItem value="jpeg">JPEG</SelectItem>
                      <SelectItem value="png">PNG</SelectItem>
                      <SelectItem value="webp">WebP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="method">Resize Method</Label>
                  <Select value={method} onValueChange={(value) => setMethod(value as ResizeMethod)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lanczos">Lanczos (Best Quality)</SelectItem>
                      <SelectItem value="bicubic">Bicubic</SelectItem>
                      <SelectItem value="bilinear">Bilinear</SelectItem>
                      <SelectItem value="nearest">Nearest Neighbor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={resizeImages} 
                  className="flex-1" 
                  disabled={images.length === 0 || isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Maximize2 className="h-4 w-4 mr-2" />
                      Resize Images
                    </>
                  )}
                </Button>
                <Button variant="outline" onClick={clearImages}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clear
                </Button>
                <Button variant="outline" onClick={downloadAll} disabled={images.length === 0 || isProcessing}>
                  <Download className="h-4 w-4 mr-2" />
                  Download All
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Image Preview */}
          {images.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Image className="h-5 w-5" />
                  Image Preview
                </CardTitle>
                <CardDescription>
                  {images.length} image{images.length > 1 ? 's' : ''} uploaded
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {images.map((image) => (
                    <div key={image.id} className="border rounded-lg p-4 space-y-3">
                      <div className="relative">
                        <img
                          src={image.preview}
                          alt={image.file.name}
                          className="w-full h-32 object-cover rounded"
                        />
                        <Button
                          size="sm"
                          variant="destructive"
                          className="absolute top-2 right-2 h-6 w-6 p-0"
                          onClick={() => removeImage(image.id)}
                        >
                          ×
                        </Button>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm font-medium truncate">{image.file.name}</p>
                        <div className="text-xs text-muted-foreground space-y-1">
                          <div>Original: {image.originalSize.width} × {image.originalSize.height}</div>
                          <div>Size: {ImageResizer.getFileSize(image.file.size)}</div>
                          {image.resizedSize.width > 0 && (
                            <div>Resized: {image.resizedSize.width} × {image.resizedSize.height}</div>
                          )}
                        </div>
                        <Button
                          size="sm"
                          className="w-full"
                          onClick={() => downloadImage(image)}
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Download
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Statistics */}
          {images.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Statistics
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">{images.length}</div>
                    <div className="text-sm text-muted-foreground">Images</div>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">{ImageResizer.getFileSize(getTotalSize)}</div>
                    <div className="text-sm text-muted-foreground">Total Size</div>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">{getAverageDimensions.width}</div>
                    <div className="text-sm text-muted-foreground">Avg Width</div>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <div className="text-2xl font-bold">{getAverageDimensions.height}</div>
                    <div className="text-sm text-muted-foreground">Avg Height</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* History */}
          {history.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="h-5 w-5" />
                  Recent History
                </CardTitle>
                <CardDescription>
                  Last {history.length} resize operation{history.length > 1 ? 's' : ''}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {history.slice(0, 10).map((entry) => (
                    <div
                      key={entry.id}
                      className="p-3 border rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="secondary" className="text-xs">
                          {entry.images.length} image{entry.images.length > 1 ? 's' : ''}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {new Date(entry.timestamp || Date.now()).toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {entry.settings.width} × {entry.settings.height}px
                        <br />
                        Quality: {entry.settings.quality}% • {entry.settings.format}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tips */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="h-5 w-5" />
                Tips
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span>Lanczos provides the best quality for most images</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span>WebP format offers better compression than JPEG</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span>Maintain aspect ratio to prevent distortion</span>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                <span>Use device presets for responsive design</span>
              </div>
            </CardContent>
          </Card>

          {/* Disclaimer */}
          <Alert>
            <Shield className="h-4 w-4" />
            <AlertDescription>
              <strong>Note:</strong> All image processing is done locally in your browser. 
              Your images are never uploaded to our servers.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    </div>
  );
}
